import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

const ROLES = [
  { value: 'admin',         label: 'Admin'          },
  { value: 'manager',       label: 'Manager'        },
  { value: 'sales_support', label: 'Sales / Support'},
  { value: 'installer',     label: 'Installer'      },
  { value: 'accountant',    label: 'Accountant'     },
];

const ROLE_STYLE = {
  admin:         { bg: '#FEF3C7', color: '#92400E' },
  manager:       { bg: '#EDE9FE', color: '#5B21B6' },
  sales_support: { bg: '#DBEAFE', color: '#1E40AF' },
  installer:     { bg: '#D1FAE5', color: '#065F46' },
  accountant:    { bg: '#FEE2E2', color: '#991B1B' },
};

const BLANK = {
  full_name: '', username: '', email: '',
  contact: '', role: 'manager', password: '', status: 'active',
};

export default function Users() {
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [stFilter, setStFilter]     = useState('');
  const [modal, setModal]           = useState(null); // 'add'|'edit'|'delete'
  const [selected, setSelected]     = useState(null);
  const [form, setForm]             = useState(BLANK);
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState('');
  const [showPwd, setShowPwd]       = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    const ok = !q
      || u.full_name?.toLowerCase().includes(q)
      || u.username?.toLowerCase().includes(q)
      || u.email?.toLowerCase().includes(q)
      || u.user_id?.toLowerCase().includes(q);
    return ok && (!roleFilter || u.role === roleFilter) && (!stFilter || u.status === stFilter);
  });

  const openAdd  = ()  => { setForm(BLANK); setErr(''); setShowPwd(false); setModal('add'); };
  const openEdit = (u) => {
    setSelected(u);
    setForm({ full_name: u.full_name || '', username: u.username, email: u.email || '', contact: u.contact || '', role: u.role, password: '', status: u.status });
    setErr(''); setShowPwd(false); setModal('edit');
  };
  const openDel  = (u) => { setSelected(u); setModal('delete'); };
  const close    = ()  => { setModal(null); setSelected(null); };

  const save = async () => {
    if (!form.full_name.trim())             return setErr('Full name is required.');
    if (!form.username.trim())              return setErr('Username is required.');
    if (modal === 'add' && !form.password)  return setErr('Password is required.');
    setSaving(true); setErr('');
    try {
      const body = { ...form };
      if (modal === 'edit' && !body.password) delete body.password;
      if (modal === 'add') await api.post('/api/users', body);
      else                  await api.put(`/api/users/${selected.id}`, body);
      load(); close();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Something went wrong.');
    } finally { setSaving(false); }
  };

  const del = async () => {
    setSaving(true);
    try { await api.delete(`/api/users/${selected.id}`); load(); close(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    await api.put(`/api/users/${u.id}`, { status: u.status === 'active' ? 'inactive' : 'active' });
    load();
  };

  const counts = {
    total:    users.length,
    active:   users.filter(u => u.status === 'active').length,
    inactive: users.filter(u => u.status !== 'active').length,
  };

  return (
    <Layout title="User Management">

      {/* ── Top row ── */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Total Users', val: counts.total,    dot: C.primary },
          { label: 'Active',      val: counts.active,   dot: C.success },
          { label: 'Inactive',    val: counts.inactive, dot: C.danger  },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '14px 22px',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <button onClick={openAdd} style={{ ...btnPrimary, marginLeft: 'auto' }}>
          + Add User
        </button>
      </div>

      {/* ── Table card ── */}
      <div style={card}>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search name, username, email, ID..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={searchBox}
          />
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={sel}>
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={sel}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {(search || roleFilter || stFilter) && (
            <button onClick={() => { setSearch(''); setRoleFilter(''); setStFilter(''); }} style={btnGhost}>
              Clear
            </button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: C.textSub }}>
            {filtered.length} / {users.length} users
          </span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '960px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['User ID', 'Full Name', 'Username', 'Official Email', 'Contact', 'Role', 'Status', 'Actions']
                  .map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={emptyCell}>Loading users...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={emptyCell}>No users found.</td></tr>
              ) : filtered.map((u, i) => (
                <tr key={u.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                  <td style={td}>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: C.primaryDeep,
                      background: C.primaryLight, padding: '2px 8px', borderRadius: '4px' }}>
                      {u.user_id}
                    </span>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={avatarStyle}>
                        {(u.full_name || u.username || '?')[0].toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600, color: C.textMain }}>{u.full_name || '—'}</span>
                    </div>
                  </td>
                  <td style={td}><span style={{ color: C.textSub }}>@{u.username}</span></td>
                  <td style={td}>{u.email || <span style={{ color: C.border }}>—</span>}</td>
                  <td style={td}>{u.contact || <span style={{ color: C.border }}>—</span>}</td>
                  <td style={td}><RoleBadge role={u.role} /></td>
                  <td style={td}>
                    <button onClick={() => toggleStatus(u)} style={{
                      border: 'none', cursor: 'pointer', borderRadius: '20px',
                      padding: '4px 12px', fontSize: '11px', fontWeight: 600,
                      background: u.status === 'active' ? '#D1FAE5' : '#FEE2E2',
                      color:      u.status === 'active' ? '#065F46' : '#991B1B',
                    }}>
                      {u.status === 'active' ? '● Active' : '○ Inactive'}
                    </button>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <Btn label="Edit"   bg="#3B82F6" onClick={() => openEdit(u)} />
                      {u.username !== 'admin' && (
                        <Btn label="Delete" bg={C.danger} onClick={() => openDel(u)} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {(modal === 'add' || modal === 'edit') && (
        <Overlay>
          <ModalBox title={modal === 'add' ? '+ Add New User' : 'Edit User'} onClose={close}>
            {err && <ErrBox msg={err} />}
            <div style={grid2}>
              <Field label="Full Name *">
                <FInput value={form.full_name} onChange={v => setForm(f => ({ ...f, full_name: v }))} placeholder="John Doe" />
              </Field>
              <Field label="Username *">
                <FInput value={form.username} onChange={v => setForm(f => ({ ...f, username: v }))} placeholder="johndoe" />
              </Field>
              <Field label="Official Email">
                <FInput type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} placeholder="john@company.com" />
              </Field>
              <Field label="Contact Number">
                <FInput type="tel" value={form.contact} onChange={v => setForm(f => ({ ...f, contact: v }))} placeholder="+1 555 000 0000" />
              </Field>
              <Field label="Role *">
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={fselect}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Field>
              <Field label="Profile Status">
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={fselect}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </Field>
            </div>
            <Field label={modal === 'add' ? 'Password *' : 'New Password (leave blank to keep current)'}>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={modal === 'add' ? 'Min. 6 characters' : '••••••••'}
                  style={{ ...inputBase, paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(p => !p)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: '16px', color: C.textSub, padding: '2px', lineHeight: 1,
                  }}
                  title={showPwd ? 'Hide password' : 'Show password'}
                >
                  {showPwd ? '🙈' : '👁'}
                </button>
              </div>
            </Field>
            <ModalFooter onClose={close} onSave={save} saving={saving} saveLabel="Save User" />
          </ModalBox>
        </Overlay>
      )}

      {/* ── Delete Modal ── */}
      {modal === 'delete' && (
        <Overlay>
          <ModalBox title="Delete User" onClose={close}>
            {err && <ErrBox msg={err} />}
            <div style={{ fontSize: '14px', color: C.textMain, lineHeight: 1.7 }}>
              Are you sure you want to delete{' '}
              <strong style={{ color: C.danger }}>{selected?.full_name || selected?.username}</strong>
              {' '}({selected?.user_id})?
              <br />
              <span style={{ color: C.textSub, fontSize: '13px' }}>This action cannot be undone.</span>
            </div>
            <ModalFooter onClose={close} onSave={del} saving={saving} saveLabel="Delete" danger />
          </ModalBox>
        </Overlay>
      )}
    </Layout>
  );
}

/* ── Sub-components ── */

function RoleBadge({ role }) {
  const s = ROLE_STYLE[role] || { bg: '#F3F4F6', color: '#374151' };
  const label = ROLES.find(r => r.value === role)?.label || role;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>
      {label}
    </span>
  );
}

function Btn({ label, bg, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
      background: `${bg}18`, color: bg, border: `1px solid ${bg}30`, cursor: 'pointer',
    }}>
      {label}
    </button>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: '20px',
    }}>
      {children}
    </div>
  );
}

function ModalBox({ title, onClose, children }) {
  return (
    <div style={{
      background: C.white, borderRadius: '14px', padding: '28px',
      width: '100%', maxWidth: '560px',
      boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
      border: `1px solid ${C.border}`, maxHeight: '90vh', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: 700, color: C.textMain }}>
          <div style={{ width: '3px', height: '16px', background: C.primary, borderRadius: '2px' }} />
          {title}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
      </div>
      {children}
    </div>
  );
}

function ModalFooter({ onClose, onSave, saving, saveLabel = 'Save', danger = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
      <button onClick={onClose} style={btnGhost}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={danger ? btnDanger : btnPrimary}>
        {saving ? 'Saving...' : saveLabel}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function FInput({ type = 'text', value, onChange, placeholder }) {
  const [focus, setFocus] = useState(false);
  return (
    <input
      type={type} value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ ...inputBase, borderColor: focus ? C.primary : C.border }}
    />
  );
}

function ErrBox({ msg }) {
  return (
    <div style={{
      background: C.dangerBg, color: C.dangerText,
      border: '1px solid #FECACA', borderRadius: '8px',
      padding: '10px 14px', marginBottom: '16px',
      fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center',
    }}>
      ⚠ {msg}
    </div>
  );
}

/* ── Styles ── */
const inputBase = {
  width: '100%', padding: '10px 12px',
  border: `1.5px solid`, borderRadius: '8px',
  fontSize: '13px', color: C.textMain,
  outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};
const fselect = {
  ...inputBase, background: C.white, cursor: 'pointer',
  borderColor: C.border,
};
const th = {
  textAlign: 'left', padding: '11px 14px',
  fontSize: '11px', fontWeight: 700, color: C.textSub,
  textTransform: 'uppercase', letterSpacing: '0.5px',
};
const td = {
  padding: '13px 14px', fontSize: '13px',
  color: C.textMain, verticalAlign: 'middle',
};
const avatarStyle = {
  width: '32px', height: '32px', borderRadius: '50%',
  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
  color: '#fff', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontWeight: 700, fontSize: '13px', flexShrink: 0,
};
const card = {
  background: C.white, borderRadius: '12px', padding: '22px',
  border: `1px solid ${C.border}`,
  boxShadow: '0 1px 3px rgba(245,166,35,0.06)',
};
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' };
const searchBox = {
  flex: 1, minWidth: '220px', padding: '9px 14px',
  border: `1.5px solid ${C.border}`, borderRadius: '8px',
  fontSize: '13px', color: C.textMain, outline: 'none',
};
const sel = {
  padding: '9px 12px', border: `1.5px solid ${C.border}`,
  borderRadius: '8px', fontSize: '13px', outline: 'none',
  background: C.white, cursor: 'pointer', color: C.textMain,
};
const emptyCell = { textAlign: 'center', padding: '40px', color: C.textSub, fontSize: '13px' };
const btnPrimary = {
  padding: '9px 20px',
  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
  color: '#fff', border: 'none', borderRadius: '8px',
  fontSize: '13px', fontWeight: 700, cursor: 'pointer',
  boxShadow: `0 3px 10px rgba(245,166,35,0.3)`,
};
const btnGhost = {
  padding: '9px 18px', background: C.primaryBg,
  color: C.textSub, border: `1px solid ${C.border}`,
  borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const btnDanger = {
  padding: '9px 20px', background: C.danger,
  color: '#fff', border: 'none', borderRadius: '8px',
  fontSize: '13px', fontWeight: 700, cursor: 'pointer',
};
